/**
 * An image or video uploaded to GitHub, in the current `user-attachments/assets` format or the
 * older repository-scoped one. In a private repository GitHub serves these only to a signed-in
 * viewer with access, so they load here only from the short-lived signed URLs that GitHub's
 * render substitutes for them.
 */
const GITHUB_UPLOAD_URL_PATTERN =
  /https:\/\/github\.com\/(?:user-attachments\/assets|[\w.-]+\/[\w.-]+\/assets\/\d+)\/[\da-f-]{36}/;

export function containsGithubUploadUrl(content: string): boolean {
  return GITHUB_UPLOAD_URL_PATTERN.test(content);
}

export async function fetchGithubMarkdown(
  content: string,
  org: string,
  repo: string,
  signal: AbortSignal,
): Promise<string> {
  const response = await fetch('/api/github/markdown', {
    method: 'POST',
    body: JSON.stringify({ content, org, repo }),
    headers: {
      'Content-Security-Policy':
        "default-src 'self'; img-src 'self' https:; style-src 'self' 'unsafe-inline'",
      'Content-Type': 'application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return response.text();
}

export type GithubMarkdownRenderBatch = {
  render: (content: string) => Promise<string>;
  abort: () => void;
};

export function createGithubMarkdownRenderBatch({
  contents,
  org,
  repo,
}: {
  contents: string[];
  org: string;
  repo: string;
}): GithubMarkdownRenderBatch {
  const uniqueContents = [...new Set(contents)];
  let controller: AbortController | undefined;
  let promise: Promise<Map<string, string>> | undefined;

  async function renderParts(signal: AbortSignal): Promise<Map<string, string>> {
    if (uniqueContents.length === 1) {
      const content = uniqueContents[0]!;
      return new Map([[content, await fetchGithubMarkdown(content, org, repo, signal)]]);
    }

    const boundary = `cubic-markdown-part-${crypto.randomUUID()}-`;
    const marker = (index: number) => `<p>${boundary}${index}</p>`;
    const framedContent =
      uniqueContents.map((content, index) => `${marker(index)}\n\n${content}\n\n`).join('') +
      marker(uniqueContents.length);
    const html = await fetchGithubMarkdown(framedContent, org, repo, signal);
    // Match only complete boundary paragraphs, including GitHub's optional attributes.
    // Validate the entire frame before exposing any slice to a child renderer.
    const markerPattern = new RegExp(
      `<p(?:\\s+(?:[^\\s>"']|"[^"]*"|'[^']*')+)*\\s*>${boundary}(\\d+)</p\\s*>`,
      'g',
    );
    const markers = [...html.matchAll(markerPattern)];
    const suffix = html.slice((markers.at(-1)?.index ?? 0) + (markers.at(-1)?.[0].length ?? 0));
    // The parser may close authored tags after the final marker when non-upload
    // closing segments were omitted; each child's sanitizer repairs its own slice.
    const hasOnlyTrailingClosingTags = /^(?:\s*<\/[a-z][a-z\d:-]*\s*>)*\s*$/i.test(suffix);
    if (
      markers.length !== uniqueContents.length + 1 ||
      markers.some((match, index) => match[1] !== String(index)) ||
      html.slice(0, markers[0]?.index).trim() ||
      !hasOnlyTrailingClosingTags
    ) {
      throw new Error('Invalid GitHub markdown batch boundaries');
    }

    return new Map(
      uniqueContents.map((content, index) => {
        const start = markers[index]!;
        const end = markers[index + 1]!;
        return [content, html.slice(start.index! + start[0].length, end.index)];
      }),
    );
  }

  return {
    async render(content) {
      if (!uniqueContents.includes(content)) {
        throw new Error('Content is not part of the GitHub markdown batch');
      }
      if (!promise) {
        controller = new AbortController();
        promise = renderParts(controller.signal);
      }
      const parts = await promise;
      return parts.get(content)!;
    },
    abort() {
      controller?.abort();
      controller = undefined;
      promise = undefined;
    },
  };
}
